import React, { useState } from "react";
import AccordionTwoColumnGrid from "@/components/molecules/AccordionTwoColumnGrid";
import { useTranslation } from "react-i18next";
import Stack from "@/components/atoms/Stack";
import Text from "@/components/atoms/Text";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { Grid, Group, Card, Badge } from "@mantine/core";
import { Control, useFieldArray, FieldErrors, useFormState } from "react-hook-form";
import SelectCountry from "@/components/molecules/SelectInputs/SelectCountry";
import {
  AddressItemSchema,
  RegisterCompanyFormInput,
} from "@/components/pages/RegisterCompany/validation/registerCompanyValidationSchema";
import TextInput from "@/components/atoms/TextInput";
import SelectAddressType from "@/components/molecules/SelectInputs/SelectAddressType";
import CTA from "@/components/atoms/CTA";
import { AddressType, PartialCreateAddress } from "@/types";
import { useGetCountries } from "@/services/RegisterCompany";
import { useAuth } from "@/contexts/AuthContext";
import { firstErrorMessage } from "@/utils/formErrors";

const ADDRESS_MAX_LENGTH = 100;
const POST_CODE_MAX_LENGTH = 10;

type AddressFieldErrors = Partial<Record<keyof PartialCreateAddress, string>>;

interface CompanyAddressProps {
  control: Control<RegisterCompanyFormInput>;
  saveDraft: () => Promise<void>;
  errors?: FieldErrors<RegisterCompanyFormInput>;
}

const emptyAddress: PartialCreateAddress = {
  address: "",
  address2: "",
  address3: "",
  postCode: "",
  place: "",
  regionId: undefined,
  countryId: 578,
  addressType: "BILLING",
};

const CompanyAddress: React.FC<CompanyAddressProps> = ({ control, saveDraft }) => {
  const [newAddress, setNewAddress] = useState(emptyAddress);
  const [fieldErrors, setFieldErrors] = useState<AddressFieldErrors>({});
  const { user } = useAuth();
  const { data: countries } = useGetCountries(user?.language.id as number, { enabled: !!user?.language.id });
  const { t } = useTranslation();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "partialCompany.address" as any,
  });

  const { errors } = useFormState({ control, name: "partialCompany.address" });

  const updateField = <K extends keyof PartialCreateAddress>(key: K, value: PartialCreateAddress[K]) => {
    setNewAddress((prev) => ({ ...prev, [key]: value }));
    if (fieldErrors[key]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleAdd = async () => {
    const result = AddressItemSchema.safeParse(newAddress);
    if (!result.success) {
      const nextErrors: AddressFieldErrors = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0] as keyof PartialCreateAddress | undefined;
        if (key && !nextErrors[key]) nextErrors[key] = issue.message;
      }
      setFieldErrors(nextErrors);
      return;
    }
    setFieldErrors({});
    append(result.data);
    setNewAddress(emptyAddress);
    await saveDraft();
  };

  const handleRemove = async (index: number) => {
    remove(index);
    await saveDraft();
  };

  const leftContent = (
    <Stack>
      <Text size="lg" display="technical" tt="uppercase" fw={500} ff="heading">
        {t("registerCompany:companyAddress.title")}
      </Text>
      <Grid>
        <Grid.Col span={1} pr={0} mr={0}>
          <SVGAsset name="circle-questionmark" />
        </Grid.Col>
        <Grid.Col span={10} pl={0} ml={0}>
          <Text size="sm" color="secondary">
            {t("registerCompany:companyAddress.reason")}
          </Text>
        </Grid.Col>
      </Grid>
    </Stack>
  );

  const rightContent = (
    <Grid>
      <Grid.Col span={12}>
        <Group>
          {fields.map((field: any, index) => (
            <Card key={field.id} shadow="xs" padding="sm" radius="md" withBorder>
              <Stack align="center" gap={2}>
                <Badge radius="sm" color="var(--color-completed-primary)">
                  {t(`enumerables:addressTypes.${field.addressType}`)}
                </Badge>
                <Text size="sm" color="primary" fw={500} tt="capitalize" mt={4}>
                  {field.address}
                </Text>
                <Text size="sm" color="secondary" fw={500} tt="capitalize">
                  {field.address2} {field.address3}
                </Text>
                <Text size="sm" color="primary" fw={500}>
                  {field.postCode} {field.place}
                </Text>
                <Text size="sm" color="secondary" ta="center" mx="auto">
                  {countries?.find((c) => c.id === field.countryId)?.name}
                </Text>
                {firstErrorMessage(errors?.partialCompany?.address?.[index]) && (
                  <Text size="sm" color="error" data-testid={`address-error-${index}`}>
                    {t(firstErrorMessage(errors?.partialCompany?.address?.[index]) as string)}
                  </Text>
                )}
                <CTA type="button" intent="negate" size="xs" mt="sm" onClick={() => handleRemove(index)}>
                  {t("registerCompany:companyAddress.removeAddress")}
                </CTA>
              </Stack>
            </Card>
          ))}
        </Group>
      </Grid.Col>
      <Grid.Col span={12}>
        <TextInput
          data-testid="address-input"
          label={t("registerCompany:companyAddress.address")}
          withAsterisk
          maxLength={ADDRESS_MAX_LENGTH}
          value={newAddress.address}
          error={fieldErrors.address ? t(fieldErrors.address) : undefined}
          onChange={(e) => updateField("address", e.target.value)}
        />
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 6 }}>
        <TextInput
          data-testid="address-input2"
          label={t("registerCompany:companyAddress.address2")}
          maxLength={ADDRESS_MAX_LENGTH}
          value={newAddress.address2}
          error={fieldErrors.address2 ? t(fieldErrors.address2) : undefined}
          onChange={(e) => updateField("address2", e.target.value)}
        />
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 6 }}>
        <TextInput
          data-testid="address-input3"
          label={t("registerCompany:companyAddress.address3")}
          maxLength={ADDRESS_MAX_LENGTH}
          value={newAddress.address3}
          error={fieldErrors.address3 ? t(fieldErrors.address3) : undefined}
          onChange={(e) => updateField("address3", e.target.value)}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 6 }}>
        <SelectAddressType
          data-testid="address-type-select"
          filterOut={fields.map((addr: any) => addr.addressType)}
          label={t("registerCompany:companyAddress.addressType")}
          onChange={(e) => updateField("addressType", e as AddressType)}
          value={newAddress.addressType}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 6 }}>
        <TextInput
          data-testid="post-code-input"
          label={t("registerCompany:companyAddress.postCode")}
          withAsterisk
          maxLength={POST_CODE_MAX_LENGTH}
          value={newAddress.postCode}
          error={fieldErrors.postCode ? t(fieldErrors.postCode) : undefined}
          onChange={(e) => updateField("postCode", e.target.value)}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 6 }}>
        <TextInput
          data-testid="place-input"
          label={t("registerCompany:companyAddress.place")}
          withAsterisk
          value={newAddress.place}
          error={fieldErrors.place ? t(fieldErrors.place) : undefined}
          onChange={(e) => updateField("place", e.target.value)}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 6 }}>
        <SelectCountry
          data-testid="country-select"
          label={t("registerCompany:companyAddress.country")}
          withAsterisk
          value={newAddress.countryId ? newAddress.countryId.toString() : ""}
          valueKey="id"
          labelKey="name"
          error={fieldErrors.countryId ? t(fieldErrors.countryId) : undefined}
          onChange={(e) => updateField("countryId", Number(e))}
        />
      </Grid.Col>
      <Grid.Col span={12}>
        {errors?.partialCompany?.address?.root?.message ? (
          <Text size="sm" color="error">
            {t(errors.partialCompany.address.root.message)}
          </Text>
        ) : (
          <Text size="sm" color="secondary">
            {t("registerCompany:companyAddress.atLeastOneBillingAddressHint")}
          </Text>
        )}
      </Grid.Col>
      <Grid.Col span={12}>
        <CTA variant="primary" data-testid="add-address-button" type={"button"} onClick={handleAdd}>
          {t("registerCompany:companyAddress.addNewAddress")}
        </CTA>
      </Grid.Col>
    </Grid>
  );

  return <AccordionTwoColumnGrid leftContent={leftContent} rightContent={rightContent} />;
};

export default CompanyAddress;
